import { Map } from 'shared/map-exports';
import { useCallback } from 'react';
import { MapIconWithRaster } from 'features/mapping/mapping.types';
import { MapIconsWithRasters } from './Icons';

type RGBAColor = [number, number, number, number];

const ESRI_MAP_SYMBOL_PREFIX = 'esri-symbol';
const ESRI_MAP_SYMBOL_DEFAULT_SYMBOL = 'circle';
const ESRI_MAP_SYMBOL_DEFAULT_COLOR: RGBAColor = [90, 90, 90, 255]; // Default color is gray
const ESRI_MAP_SYMBOL_STROKE_INNER_WIDTH_PX = 3; // Stroke width for the black outline of the symbol
const ESRI_MAP_SYMBOL_STROKE_OUTER_WIDTH_PX = 1; // Stroke width for the white outline of the symbol
const ESRI_MAP_SYMBOL_SIZE_PX = 25; // Size of the symbol's main color area
const ESRI_MAP_SYMBOL_SIZE_SCALE_FACTOR = 4; // Scale factor so we can generate one larger symbol and have it still look good when it's big
const COLOR_BLACK: RGBAColor = [0, 0, 0, 255]; // Black color for the inner stroke
const COLOR_WHITE: RGBAColor = [255, 255, 255, 255]; // White color for the outer stroke

// Matches the ESRI symbol style encoded in the icon's name:
//
// esri-symbol-{shape}-{red}-{green}-{blue}-{alpha}
//
// where red/green/blue/alpha are integers in the range 0-255,
// and shape is one of: circle, cross, diamond, square, x.
//
// See: https://developers.arcgis.com/web-map-specification/objects/esriSMS_symbol/
type EsriMarkerTypes =
  | 'circle'
  | 'triangle'
  | 'cross'
  | 'diamond'
  | 'square'
  | 'x';
const esriMapSymbolRegex = new RegExp(
  `^${ESRI_MAP_SYMBOL_PREFIX}-(circle|triangle|cross|diamond|square|x)-([0-9]+)-([0-9]+)-([0-9]+)-([0-9]+)$`,
);

type UseHandleMissingMapStyleImageReturn = {
  handleMissingStyleImage: (event: { id: string }) => void;
};

const parseEsriSymbolFromIconName = (
  iconName: string,
): { symbol: EsriMarkerTypes; color: RGBAColor } => {
  const match = esriMapSymbolRegex.exec(iconName);

  if (!match) {
    return {
      color: ESRI_MAP_SYMBOL_DEFAULT_COLOR,
      symbol: ESRI_MAP_SYMBOL_DEFAULT_SYMBOL,
    };
  }

  const [, symbol, r, g, b, a] = match;

  return {
    color: [parseInt(r, 10), parseInt(g, 10), parseInt(b, 10), parseInt(a, 10)],
    symbol: symbol as EsriMarkerTypes,
  };
};

const makeStyleColor = ([r, g, b, a]: RGBAColor): string =>
  `rgba(${r}, ${g}, ${b}, ${a / 255})`;

// Helper functions for canvas operations
const clearAndFill = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
): void => {
  // Clears the canvas in the current path and then fills it with the current fill style. Necessary
  // to correctly render symbols with alpha.
  ctx.clip();
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fill();
};

const clearAndStroke = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
): void => {
  // Clears the pixels that will be stroked, then strokes the path. Necessary
  // to correctly render symbols with alpha.
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;

  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      if (ctx.isPointInStroke(x, y)) {
        const index = (y * canvas.width + x) * 4;
        data[index + 3] = 0; // alpha of 0 = transparent
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
  ctx.stroke();
};

// Drawing functions for different symbol shapes
type DrawShapeParams = {
  ctx: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  canvasSize: number;
  size: number;
  color: RGBAColor;
  ratio: number;
  innerStrokeWidth: number;
  outerStrokeWidth: number;
  innerStrokeWidth2: number;
  outerStrokeWidth2: number;
};

const drawCircle = (params: DrawShapeParams): void => {
  const {
    ctx,
    canvas,
    canvasSize,
    size,
    color,
    innerStrokeWidth2,
    outerStrokeWidth2,
  } = params;
  // draw a circle in the center of the canvas. to simulate outer strokes, we draw three circles:
  // 1) white for outermost stroke
  // 2) black for inner stroke
  // 3) colored circle for the symbol itself
  ctx.beginPath();
  const makeArcPath = (radius: number): void => {
    ctx.arc(canvasSize / 2, canvasSize / 2, radius, 0, Math.PI * 2);
  };
  makeArcPath((size + innerStrokeWidth2 + outerStrokeWidth2) / 2);
  ctx.fillStyle = makeStyleColor(COLOR_WHITE);
  ctx.fill();

  ctx.beginPath();
  makeArcPath((size + innerStrokeWidth2) / 2);
  ctx.fillStyle = makeStyleColor(COLOR_BLACK);
  ctx.fill();

  ctx.beginPath();
  makeArcPath(size / 2);
  ctx.fillStyle = makeStyleColor(color);
  clearAndFill(ctx, canvas);
};

const drawDiamond = (params: DrawShapeParams): void => {
  const {
    ctx,
    canvas,
    canvasSize,
    color,
    innerStrokeWidth,
    outerStrokeWidth,
    innerStrokeWidth2,
    outerStrokeWidth2,
  } = params;
  // this time draw the path of the diamond shape, do the two strokes but double-thickness so that once
  // the main body is filled, the strokes appear as outer strokes.
  ctx.beginPath();
  const pad = (outerStrokeWidth + innerStrokeWidth) * Math.SQRT2;
  ctx.moveTo(canvasSize / 2, pad);
  ctx.lineTo(canvasSize - pad, canvasSize / 2);
  ctx.lineTo(canvasSize / 2, canvasSize - pad);
  ctx.lineTo(pad, canvasSize / 2);
  ctx.closePath();

  // and stroke with a white outline
  ctx.lineWidth = outerStrokeWidth2 + innerStrokeWidth2;
  ctx.strokeStyle = makeStyleColor(COLOR_WHITE);
  ctx.stroke();

  // stroke the diamond with a black outline
  ctx.lineWidth = innerStrokeWidth2;
  ctx.strokeStyle = makeStyleColor(COLOR_BLACK);
  ctx.stroke();

  ctx.fillStyle = makeStyleColor(color);
  clearAndFill(ctx, canvas);
};

const drawSquare = (params: DrawShapeParams): void => {
  // Exclude 'size' since we don't use it in this function
  const {
    ctx,
    canvas,
    canvasSize,
    color,
    innerStrokeWidth,
    outerStrokeWidth2,
    innerStrokeWidth2,
  } = params;
  // same approach as the diamond
  ctx.beginPath();
  const pad = outerStrokeWidth2 + innerStrokeWidth;
  ctx.rect(pad, pad, canvasSize - pad * 2, canvasSize - pad * 2);
  ctx.closePath();

  // and stroke with a white outline
  ctx.lineWidth = outerStrokeWidth2 + innerStrokeWidth2;
  ctx.strokeStyle = makeStyleColor(COLOR_WHITE);
  ctx.stroke();

  // stroke the square with a black outline
  ctx.lineWidth = innerStrokeWidth2;
  ctx.strokeStyle = makeStyleColor(COLOR_BLACK);
  ctx.stroke();

  ctx.fillStyle = makeStyleColor(color);
  clearAndFill(ctx, canvas);
};

const drawTriangle = (params: DrawShapeParams): void => {
  const {
    ctx,
    canvas,
    canvasSize,
    size,
    color,
    innerStrokeWidth,
    outerStrokeWidth,
    innerStrokeWidth2,
    outerStrokeWidth2,
  } = params;
  // same approach as the circle
  const makeTrianglePath = (radiusIn: number): void => {
    const radius = radiusIn - innerStrokeWidth - outerStrokeWidth;
    const center = canvasSize / 2;
    const rotation = Math.PI / 6; // 30 degrees to rotate the triangle upright
    ctx.beginPath();
    ctx.moveTo(
      center + Math.cos(rotation) * radius,
      center + Math.sin(rotation) * radius,
    );
    ctx.lineTo(
      center + Math.cos((2 * Math.PI) / 3 + rotation) * radius,
      center + Math.sin((2 * Math.PI) / 3 + rotation) * radius,
    );
    ctx.lineTo(
      center + Math.cos((4 * Math.PI) / 3 + rotation) * radius,
      center + Math.sin((4 * Math.PI) / 3 + rotation) * radius,
    );
    ctx.closePath();
  };

  makeTrianglePath(size / 2 + innerStrokeWidth2 + outerStrokeWidth2);
  ctx.fillStyle = makeStyleColor(COLOR_WHITE);
  ctx.fill();

  makeTrianglePath(size / 2 + innerStrokeWidth2);
  ctx.fillStyle = makeStyleColor(COLOR_BLACK);
  ctx.fill();

  makeTrianglePath(size / 2);
  ctx.fillStyle = makeStyleColor(color);
  clearAndFill(ctx, canvas);
};

const drawCross = (params: DrawShapeParams): void => {
  const {
    ctx,
    canvas,
    canvasSize,
    size,
    color,
    ratio,
    innerStrokeWidth2,
    outerStrokeWidth2,
  } = params;
  // draw a cross in the center of the canvas. to simulate outer strokes, we draw three crosses:
  // 1) white for outermost stroke
  // 2) black for inner stroke
  // 3) colored cross for the symbol itself
  // and each is drawn using a stroke on a slightly smaller and smaller cross
  const crossStrokeWidth = 5 * ratio;
  const makeCrossPath = (length: number): void => {
    const pad = (canvasSize - length) / 2;
    ctx.beginPath();
    ctx.moveTo(canvasSize / 2, pad);
    ctx.lineTo(canvasSize / 2, canvasSize - pad);
    ctx.moveTo(pad, canvasSize / 2);
    ctx.lineTo(canvasSize - pad, canvasSize / 2);
  };

  makeCrossPath(size + innerStrokeWidth2 + outerStrokeWidth2);
  ctx.lineWidth = crossStrokeWidth + innerStrokeWidth2 + outerStrokeWidth2;
  ctx.strokeStyle = makeStyleColor(COLOR_WHITE);
  ctx.stroke();

  makeCrossPath(size + innerStrokeWidth2);
  ctx.lineWidth = crossStrokeWidth + innerStrokeWidth2;
  ctx.strokeStyle = makeStyleColor(COLOR_BLACK);
  ctx.stroke();

  makeCrossPath(size);
  ctx.strokeStyle = makeStyleColor(color);
  ctx.lineWidth = crossStrokeWidth;
  clearAndStroke(ctx, canvas);
};

const drawX = (params: DrawShapeParams): void => {
  const {
    ctx,
    canvas,
    canvasSize,
    size,
    color,
    ratio,
    innerStrokeWidth,
    outerStrokeWidth,
    innerStrokeWidth2,
    outerStrokeWidth2,
  } = params;
  // same basic approach as cross.
  const crossStrokeWidth = 5 * ratio;
  const crossExtraPadding = innerStrokeWidth + outerStrokeWidth;
  const makeXPath = (length: number): void => {
    ctx.beginPath();
    const pad =
      ((canvasSize - length) / 2 + crossExtraPadding) / Math.SQRT2 +
      outerStrokeWidth;
    ctx.moveTo(pad, pad);
    ctx.lineTo(canvasSize - pad, canvasSize - pad);
    ctx.moveTo(canvasSize - pad, pad);
    ctx.lineTo(pad, canvasSize - pad);
  };

  makeXPath(size + innerStrokeWidth2 + outerStrokeWidth2);
  ctx.lineWidth = crossStrokeWidth + innerStrokeWidth2 + outerStrokeWidth2;
  ctx.strokeStyle = makeStyleColor(COLOR_WHITE);
  ctx.stroke();

  makeXPath(size + innerStrokeWidth2);
  ctx.lineWidth = crossStrokeWidth + innerStrokeWidth2;
  ctx.strokeStyle = makeStyleColor(COLOR_BLACK);
  ctx.stroke();

  makeXPath(size);
  ctx.lineWidth = crossStrokeWidth;
  ctx.strokeStyle = makeStyleColor(color);
  clearAndStroke(ctx, canvas);
};

const generateEsriSymbol = (iconName: string): ImageData => {
  const { color, symbol } = parseEsriSymbolFromIconName(iconName);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to create canvas context for ESRI symbol');
  }

  // convenience constants so we do less math below. all of these are in units of actual pixels (ie, taking into
  // account the device pixel ratio and the scale factor).
  const ratio =
    (window.devicePixelRatio || 1) * ESRI_MAP_SYMBOL_SIZE_SCALE_FACTOR;
  const canvasSize =
    (ESRI_MAP_SYMBOL_SIZE_PX +
      (ESRI_MAP_SYMBOL_STROKE_INNER_WIDTH_PX +
        ESRI_MAP_SYMBOL_STROKE_OUTER_WIDTH_PX) *
        2) * // canvas size needs to accommodate the outer and inner strokes as well
    ratio;
  const size = ESRI_MAP_SYMBOL_SIZE_PX * ratio;
  const innerStrokeWidth = ESRI_MAP_SYMBOL_STROKE_INNER_WIDTH_PX * ratio;
  const outerStrokeWidth = ESRI_MAP_SYMBOL_STROKE_OUTER_WIDTH_PX * ratio;
  const innerStrokeWidth2 = innerStrokeWidth * 2;
  const outerStrokeWidth2 = outerStrokeWidth * 2;

  canvas.width = canvasSize;
  canvas.height = canvasSize;

  const drawParams: DrawShapeParams = {
    ctx,
    canvas,
    canvasSize,
    size,
    color,
    ratio,
    innerStrokeWidth,
    outerStrokeWidth,
    innerStrokeWidth2,
    outerStrokeWidth2,
  };

  const implMap = {
    circle: drawCircle,
    diamond: drawDiamond,
    square: drawSquare,
    triangle: drawTriangle,
    cross: drawCross,
    x: drawX,
  };

  const drawSymbol = implMap[symbol];
  drawSymbol?.(drawParams);

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

export const useHandleMissingMapStyleImage = (
  map: Map | undefined,
): UseHandleMissingMapStyleImageReturn => {
  const addMapIconWithRaster = useCallback(
    (icon: MapIconWithRaster) => {
      // eslint-disable-next-line no-param-reassign
      icon.img.width = icon.width * window.devicePixelRatio;
      // eslint-disable-next-line no-param-reassign
      icon.img.height = icon.height * window.devicePixelRatio;

      map?.addImage(icon.name, icon.img, {
        pixelRatio: window.devicePixelRatio,
        // content, stretchX, and stretchY, if provided, give maplibre hints
        // about the bounding box that should be used to display content within
        // a symbol, and what pixel ranges are safe to stretch when resizing
        // the symbol.
        // @ts-expect-error property is specific to the maplibre-gl library
        content: icon.content?.map((pos) => pos * window.devicePixelRatio),
        stretchX: icon.stretchX?.map(([x, y]) => [
          x * window.devicePixelRatio,
          y * window.devicePixelRatio,
        ]),
        stretchY: icon.stretchY?.map(([x, y]) => [
          x * window.devicePixelRatio,
          y * window.devicePixelRatio,
        ]),
        sdf: icon.sdf,
      });
    },
    [map],
  );

  // Icons are unloaded from the map every time the map base layer changes.
  // Registering for 'styleimagemissing' events allows for calling map.addImage()
  // (so long as it is in the body of the callback!) to load missing icon images.
  const handleMissingStyleImage = useCallback(
    (event: { id: string }) => {
      if (!map) return;

      if (event.id.startsWith(ESRI_MAP_SYMBOL_PREFIX)) {
        // If the icon is an ESRI symbol, we need to generate it on the fly.
        const image = generateEsriSymbol(event.id);
        map.addImage(event.id, image, {
          pixelRatio: window.devicePixelRatio,
        });
        return;
      }

      const icon = MapIconsWithRasters.find((i) => i.name === event.id);
      if (!icon) return;

      addMapIconWithRaster(icon);
    },
    [addMapIconWithRaster, map],
  );

  return { handleMissingStyleImage };
};
